import React, { useState } from "react";
import { Col } from "react-bootstrap";

import Control from "../../../assets/vfx/controls/control";
import Form from "../../../assets/vfx/form/form";
import { Portal } from "../../../assets/vfx/portal/portal";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";

import { PlanService } from "./planService";
import SubscriptionPlanGrid from "./subscription/subscriptionPlanGrid";

import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, defaultSearchQuery } from "../../../assets/types";
import { SearchDto, SearchOperator } from "../../../assets/utils/lib/searchDto";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { ExpandButton } from "../../common/Buttons";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { GridPlanDto } from "../../../services/dto";

interface PlanPageProps {
  controlStyles?: IControlStyles;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  onSubscriptionPlanCustomEvent?: (actionType: string, dataId: string) => void;
}

const DATA_ID_FIELD = "id";

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    alignment: "center",
    data: DATA_ID_FIELD,
    title: "",
    columnType: "custom",
    sortable: false,
    visible: true,
    width: "7%",
    customCell: (id: string, rowData: GridPlanDto) =>
      rowData?.hasSubscriptions ? <ExpandButton /> : <></>,
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "34%",
  },
  {
    data: "activationDate",
    title: "Activation Date",
    visible: true,
    columnType: "date",
    alignment: "left",
    width: "24%",
  },
];

export const PlanPage = (props: PlanPageProps) => {
  const [searchQuery, setSearchQuery] = useState<SearchDto>(defaultSearchQuery);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [activeRowData, setActiveRowData] = useState<Record<string, any>>({});

  const styles: IControlStyles = props.controlStyles ?? useControlStyles();
  const planGridQuery = PlanService.gridQuery(searchQuery);
  const isGridLoading = planGridQuery.isLoading || planGridQuery.isFetching;

  const plans: GridPlanDto[] = planGridQuery.data?.result ?? [];
  const hasData: boolean = plans.length > 0;
  const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
    GRID_ACTION_TYPES.NEW_SIBLING,
  ]);
  const layoutOptions = createLayoutOptions(hasData);

  const handleSearch = (values: any): void => {
    const filters = [];

    if (values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  const handleNew = (): void =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  const handleExpandRow = (rowData: any, rowId: string): void => {
    setActiveRowData((prev) => ({
      ...prev,
      [rowId]: rowData,
    }));

    setExpandedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const renderNestedComponents = (): (React.JSX.Element | null)[] =>
    expandedRowIds.map((id) => {
      const container = document.getElementById(id);

      if (!container) return null;
      const rowData = activeRowData[id];

      return (
        <Portal key={id} containerId={id}>
          <div className="ps-5 pe-3">
            <SubscriptionPlanGrid
              planId={rowData?.id}
              onCustomEvent={props.onSubscriptionPlanCustomEvent}
            />
          </div>
        </Portal>
      );
    });

  return (
    <PageContent title="Plans">
      <SearchContainer
        initialValues={{ name: Constants.emptyString }}
        enableReinitialize
        onSearch={handleSearch}
        onClear={handleClear}
        onNew={handleNew}
      >
        <Col lg={4}>
          <Form.Text name="name" label="Name" />
        </Col>
      </SearchContainer>

      <Control.DataGrid
        dataId={DATA_ID_FIELD}
        cols={gridColumns}
        actionCol={actionColumn}
        data={plans}
        isActionButtonClick={true}
        layoutOptions={layoutOptions}
        onCustomEvent={props.onCustomEvent}
        onParentRowClick={handleExpandRow}
        totalRecords={planGridQuery.data?.totalSize ?? plans?.length ?? 0}
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) => {
          setSearchQuery((query) => ({
            ...query,
            ...patch,
            sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
          }));
        }}
      />
      {renderNestedComponents()}
    </PageContent>
  );
};
